import React, { useState } from 'react'
import { TextField } from '@mui/material'
import { fmt, pct } from './columnasPrecios'

/**
 * Los márgenes de UNA moto, una tarjeta por clasificación.
 *
 * Existe para que Trade y Back Comercial muestren lo MISMO. Antes eran dos pantallas distintas para
 * el mismo dato: Trade dibujaba tarjetas y Back Comercial una lista de filas con un input siempre
 * abierto, así que el mismo margen se leía de dos formas y comparar entre pestañas obligaba a
 * traducir. Ahora las dos usan estas tarjetas; lo único que cambia es si se pueden tocar.
 *
 *   Trade           -> `editable={false}`. Informativo: Trade pone el PVP, no la política de margen.
 *   Back Comercial  -> `editable`. Clic en la tarjeta y se edita ahí mismo.
 *
 * El valor tecleado NO vive acá: se sube al padre por `onEditChange`, porque es el padre el que sabe
 * guardarlo y recalcular el plano. Acá solo se recuerda cuál tarjeta está abierta.
 */

/** Estas fórmulas no llevan margen: 'base' es el precio sin impuestos y 'pvp' es el PVP tal cual. */
const SIN_MARGEN = ['base', 'pvp', 'fijo']

/** Texto del pie de la tarjeta: de dónde sale el porcentaje. */
const textoOrigen = (m, propia) => {
  if (propia) return 'excepción propia de esta moto'
  const mapa = {
    marca: 'regla de la marca',
    global: 'regla general',
    cliente: 'margen negociado del cliente',
    cliente_modelo: 'negociación del cliente para esta moto',
    cliente_marca: 'negociación del cliente para la marca',
    // f51: no tiene margen propio, toma el de la clasificación de la que hereda
    heredado: `heredado de ${m.hereda_de_label || 'otra clasificación'}`,
    sin_regla: 'sin regla',
  }
  return mapa[m.origen] || m.origen || 'sin regla'
}

const MargenTarjetas = ({
  items = [],
  editable = false,
  edits = {},
  onEditChange = null,
}) => {
  // Cuál tarjeta está abierta para editar. Solo aplica con `editable`.
  const [abierto, setAbierto] = useState(null)

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {items.map((m) => {
        const sinMargen = SIN_MARGEN.includes(m.formula)
        const propia = Number(m.regla_propia_id || 0) > 0
        const tocado = edits[m.lista_id] !== undefined
        const puedeEditar = editable && !sinMargen
        const editando = puedeEditar && abierto === m.lista_id
        // Con `sin_regla` el back manda margen 0, pero no hay nada cargado: se muestra «sin margen» y el
        // campo vacío, para que teclear 0 sí cuente como cambio y se pueda guardar un 0 % de verdad.
        const actual = m.origen === 'sin_regla' || m.margen === null || m.margen === undefined
          ? null : Number(m.margen) * 100
        // Distinto de `sinMargen`: ahí la fórmula no lleva margen y está bien. Acá sí lo lleva y no
        // hay ninguno que aplicar, ni propio ni heredado. Es un dato que falta.
        const faltaMargen = !sinMargen && (m.origen === 'sin_regla' || actual === null)

        // Rojo = no resuelve ningún margen y hay que ponerlo. Amarillo = tiene regla propia.
        // Verde = lo estás cambiando ahora y todavía no se guardó. El rojo gana: que el dato falte
        // pesa más que de dónde sale, y sin él no se puede armar el plano.
        const borde = tocado ? '#1f9d55' : (faltaMargen ? '#d64545' : (propia ? '#e0a300' : '#c9d2db'))
        const fondo = tocado ? '#f2fbf5' : (faltaMargen ? '#fdecec' : (propia ? '#fff8e8' : '#fff'))

        // En modo informativo la tarjeta no lleva NINGÚN manejador: no es un botón disfrazado.
        const interactivo = puedeEditar
          ? {
            role: 'button',
            tabIndex: editando ? -1 : 0,
            onClick: () => setAbierto(m.lista_id),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setAbierto(m.lista_id)
              }
            },
          }
          : {}

        return (
          <div
            key={m.lista_id}
            {...interactivo}
            style={{
              border: `1px solid ${tocado ? '#a9dfc0' : (faltaMargen ? '#f3b6b6' : (propia ? '#f0d089' : '#e3e7ec'))}`,
              borderLeft: `4px solid ${borde}`,
              background: fondo,
              borderRadius: 8,
              padding: '8px 12px 10px',
              minWidth: 232,
              cursor: puedeEditar && !editando ? 'pointer' : 'default',
            }}
            title={puedeEditar && !editando ? 'Clic para cambiar el margen' : undefined}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f2439' }}>{m.lista_label}</div>
            <div style={{ fontSize: 10.5, color: '#8a94a0', marginBottom: 5 }}>
              {m.tabla_sap} · {m.condicion}
              {m.hereda_de_label && ` · hereda de ${m.hereda_de_label}`}
            </div>

            {sinMargen && (
              <div style={{ fontSize: 12, color: '#6b7684' }}>
                no lleva margen ·{' '}
                <b style={{ color: '#0f2439', fontVariantNumeric: 'tabular-nums' }}>{fmt(m.valor)}</b>
              </div>
            )}

            {!sinMargen && editando && (
              <TextField
                size="small"
                autoFocus
                value={tocado ? edits[m.lista_id] : (actual === null ? '' : String(Number(actual.toFixed(2))))}
                onChange={(e) => onEditChange && onEditChange(m.lista_id, e.target.value)}
                onBlur={() => setAbierto(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') setAbierto(null)
                }}
                placeholder={actual === null ? 'sin margen' : ''}
                error={actual === null && !tocado}
                InputProps={{ endAdornment: <span style={{ fontSize: 12, color: '#6b7684' }}>%</span> }}
                sx={{ width: 118, '& input': { padding: '6px 8px', fontSize: 15, fontWeight: 700 } }}
              />
            )}

            {!sinMargen && !editando && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 17, fontWeight: 700, color: tocado ? '#166534' : (propia ? '#8a5a00' : '#0f2439') }}>
                  {tocado ? `${edits[m.lista_id] || '—'} %` : (actual === null ? 'sin margen' : pct(m.margen))}
                </span>
                <span style={{ fontSize: 12, color: '#6b7684', fontVariantNumeric: 'tabular-nums' }}>
                  {m.condicion === 'ZDPC' ? '% de descuento' : `→ ${fmt(m.valor)}`}
                </span>
              </div>
            )}

            {!sinMargen && (
              <div style={{ fontSize: 10.5, color: tocado ? '#166534' : '#8a94a0', marginTop: 3 }}>
                {tocado ? 'sin guardar todavía' : textoOrigen(m, propia)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default MargenTarjetas
